/**
 * Выборка отсмотров — экран супер-админа.
 *
 * План: docs/PEDAGOGY_CROSS_REVIEW_PLAN.md §10.3
 * Решения: Р4 (два типа пар), Р7 (ось засекречена целиком), Р15.
 *
 * Живёт ВНЕ педагогического раздела и намеренно называется нейтрально: путь
 * экрана попадает в `app_events` через `logScreenVisit`, а слова «пары»,
 * «калибровка» и «контроль» в журнале посещений — это и есть та утечка, от
 * которой защищает вся остальная конструкция.
 *
 * Защита двойная и обе половины обязательны: `SuperAdminRoute` на маршруте
 * закрывает экран, `public.is_super_admin()` внутри каждой RPC закрывает
 * данные. Одного маршрута мало — RPC зовут напрямую.
 *
 * Что здесь читать. «Куратор + независимый» сам по себе не значит ничего:
 * разница может быть и надбавкой своим, и просто разной строгостью оценщиков.
 * Смысл появляется только в паре со строкой «два независимых» — она и есть
 * поправка на строгость (решение Р4). Пока во второй строке мало пар,
 * первую интерпретировать нельзя.
 */

import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Badge, Card } from '@/components/ui'
import {
  getPedagogyReviewPairSummary,
  listPedagogyReviewPairs,
  type PedagogyReviewPair,
  type PedagogyReviewPairSummary,
} from '@/services/pedagogyReviewQueueService'

const PAIR_KIND_LABEL: Record<PedagogyReviewPair['pairKind'], string> = {
  curator_vs_independent: 'Куратор + независимый',
  independent_vs_independent: 'Два независимых',
}

function formatDate(value: string): string {
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return value
  return `${day}.${month}.${year}`
}

function formatPercent(value: number | null): string {
  return value == null ? '—' : `${value.toFixed(1)}%`
}

function formatDelta(value: number | null): string {
  if (value == null) return '—'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(1)} п.п.`
}

export function ReviewSamplingPage() {
  const [pairs, setPairs] = useState<PedagogyReviewPair[]>([])
  const [summary, setSummary] = useState<PedagogyReviewPairSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [nextPairs, nextSummary] = await Promise.all([
        listPedagogyReviewPairs(null),
        getPedagogyReviewPairSummary(null),
      ])
      setPairs(nextPairs)
      setSummary(nextSummary)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const hasControlBaseline = summary.some(
    row => row.pairKind === 'independent_vs_independent' && row.pairsComplete > 0
  )

  if (loading && pairs.length === 0 && summary.length === 0) {
    return (
      <div className="space-y-4 pb-24">
        <Card>
          <div className="py-6 text-center text-sm text-muted-foreground">Загружаем выборку…</div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-24">
      {error && (
        <Card className="border border-error/40">
          <div className="flex items-start gap-2 text-sm text-error">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {summary.length === 0 ? (
        <Card>
          <div className="py-6 text-center text-sm text-muted-foreground">
            Набранных пар пока нет.
          </div>
        </Card>
      ) : (
        <Card>
          <div className="space-y-3">
            <div className="text-sm font-medium text-foreground">Свод</div>
            {summary.map(row => (
              <div key={row.pairKind} className="space-y-1 border-t border-border pt-3 first:border-0 first:pt-0">
                <div className="text-sm text-foreground">{PAIR_KIND_LABEL[row.pairKind]}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <span>
                    Пар: {row.pairsComplete} из {row.pairsTotal} с обеими оценками
                  </span>
                  <span>Средняя разница: {formatDelta(row.avgDelta)}</span>
                  <span>Разброс: {row.stddevDelta == null ? '—' : row.stddevDelta.toFixed(1)}</span>
                  <span>Медиана: {formatDelta(row.medianDelta)}</span>
                </div>
              </div>
            ))}

            {!hasControlBaseline && (
              <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                Пар «два независимых» ещё нет. Без них разницу в строке «куратор + независимый»
                нельзя отличить от разной строгости оценщиков — интерпретировать рано.
              </div>
            )}
          </div>
        </Card>
      )}

      {pairs.map(pair => (
        <Card key={pair.pairGroupId}>
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-sm text-foreground">
                {formatDate(pair.trainingDate)}, {pair.trainingTime.slice(0, 5)}
                {pair.cityName ? ` · ${pair.cityName}` : ''}
                {pair.teacherNameRaw ? ` · ${pair.teacherNameRaw}` : ''}
              </div>
              <Badge variant={pair.isComplete ? 'success' : 'neutral'}>
                {pair.isComplete ? formatDelta(pair.deltaPercent) : 'Не закрыта'}
              </Badge>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="rounded-lg bg-muted p-2 text-sm">
                <div className="text-muted-foreground">
                  Официальный{pair.officialIsCurator ? ' · куратор' : ''}
                </div>
                <div className="text-foreground">
                  {pair.officialName ?? pair.officialUserId} — {formatPercent(pair.officialPercent)}
                </div>
              </div>
              <div className="rounded-lg bg-muted p-2 text-sm">
                <div className="text-muted-foreground">
                  Контрольный{pair.controlIsCurator ? ' · куратор' : ''}
                </div>
                <div className="text-foreground">
                  {pair.controlName ?? pair.controlUserId} — {formatPercent(pair.controlPercent)}
                </div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">{PAIR_KIND_LABEL[pair.pairKind]}</div>
          </div>
        </Card>
      ))}

      <Card>
        <div className="text-sm text-muted-foreground">
          Результат используется для решения о процессе, а не как основание для взысканий:
          куратор знает контекст педагога, а сама механика скрытая. Любые персональные выводы —
          через отдельную открытую процедуру.
        </div>
      </Card>
    </div>
  )
}
